import React, { useState, useEffect } from 'react';
import { Table, Clock, Lock, Unlock, AlertCircle, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { useWeb3 } from '../hooks/useWeb3';
import { LockTableData } from '../types/contract';
import { formatTokenAmount, formatDate, getLockStatus, TOKENLOCKER_ABI, CONTRACT_ADDRESSES } from '../utils/contracts';
import { ethers } from 'ethers';
import { ERC20_ABI } from '../utils/contracts';

interface AllLocksTableProps {
  onRefresh?: () => void;
}

interface TokenGroup {
  symbol: string;
  tokenAddress: string;
  totalLocks: number;
  totalAmount: bigint;
  locks: (LockTableData & { owner: string })[];
  isExpanded: boolean;
}

export const AllLocksTable: React.FC<AllLocksTableProps> = ({ onRefresh: _onRefresh }) => {
  const { isConnected, signer } = useWeb3();
  
  const getContractInstance = () => {
    if (!signer || !isConnected) return null;
    return new ethers.Contract(CONTRACT_ADDRESSES.TOKENLOCKER, TOKENLOCKER_ABI, signer);
  };
  
  const [tokenGroups, setTokenGroups] = useState<TokenGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLocks, setTotalLocks] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 25;

  // Helper function to get token info
  const getTokenInfo = async (tokenAddress: string) => {
    if (!signer) return null;
    
    try {
      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
      const [name, symbol, decimals] = await Promise.all([
        contract.name(),
        contract.symbol(),
        contract.decimals()
      ]);
      return { name, symbol, decimals };
    } catch (error) {
      console.error('Error fetching token info:', error);
      return null;
    }
  };

  // Fetch all locks with pagination and group by token
  const fetchAllLocks = async () => {
    if (!isConnected) {
      setTokenGroups([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const contract = getContractInstance();
      if (!contract) return;

      // Get total number of locks
      const nextId = await contract.nextLockId();
      const total = Number(nextId);
      setTotalLocks(total);
      setTotalPages(Math.ceil(total / itemsPerPage));

      // Calculate start and end indices for current page
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, total);

      const allLocks: (LockTableData & { owner: string })[] = [];

      // Fetch locks for current page
      for (let i = startIndex; i < endIndex; i++) {
        try {
          const lockId = BigInt(i);
          const lock = await contract.getLock(lockId);
          
          // Get token info
          const tokenInfo = await getTokenInfo(lock.token);
          
          const lockData: LockTableData & { owner: string } = {
            id: lock.id,
            symbol: tokenInfo?.symbol || 'Unknown',
            tokenAddress: lock.token,
            type: getLockStatus(lock.unlockDate, lock.withdrawn),
            startDate: new Date(Number(lock.unlockDate) * 1000), // Using unlockDate as start for now
            unlockDate: new Date(Number(lock.unlockDate) * 1000),
            amountLocked: lock.amount,
            canWithdraw: false, // No withdraw option in this view
            owner: lock.owner
          };

          allLocks.push(lockData);
        } catch (error) {
          console.error('Error processing lock:', error);
        }
      }

      // Group locks by token
      const groupsMap = new Map<string, TokenGroup>();
      
      allLocks.forEach(lock => {
        const key = lock.tokenAddress.toLowerCase();
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            symbol: lock.symbol,
            tokenAddress: lock.tokenAddress,
            totalLocks: 0,
            totalAmount: 0n,
            locks: [],
            isExpanded: false
          });
        }
        
        const group = groupsMap.get(key)!;
        group.totalLocks++;
        group.totalAmount += lock.amountLocked;
        group.locks.push(lock);
      });

      // Convert to array and sort by total amount
      const groups = Array.from(groupsMap.values()).sort((a, b) => {
        if (a.totalAmount > b.totalAmount) return -1;
        if (a.totalAmount < b.totalAmount) return 1;
        return a.symbol.localeCompare(b.symbol);
      });

      setTokenGroups(groups);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch locks');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllLocks();
  }, [isConnected, currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const toggleGroup = (tokenAddress: string) => {
    setTokenGroups(prev => prev.map(group => 
      group.tokenAddress.toLowerCase() === tokenAddress.toLowerCase()
        ? { ...group, isExpanded: !group.isExpanded }
        : group
    ));
  };

  const getStatusIcon = (type: string) => {
    switch (type) {
      case 'Locked':
        return <Lock className="w-4 h-4 text-yellow-400" />;
      case 'Unlocked':
        return <Unlock className="w-4 h-4 text-green-400" />;
      case 'Withdrawn':
        return <AlertCircle className="w-4 h-4 text-blue-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getStatusColor = (type: string) => {
    switch (type) {
      case 'Locked':
        return 'text-yellow-400';
      case 'Unlocked':
        return 'text-green-400';
      case 'Withdrawn':
        return 'text-blue-400';
      default:
        return 'text-zinc-400';
    }
  };

  if (!isConnected) {
    return (
      <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-6">
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <p className="text-zinc-400">Please connect your wallet to view all locks</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-400/20 rounded-lg">
            <Table className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-zenjirou text-white">All Locks</h2>
            <p className="text-zinc-400 text-sm">Browse all token locks grouped by token</p>
          </div>
        </div>
        <button
          onClick={fetchAllLocks}
          disabled={isLoading}
          className="p-2 bg-zinc-700/50 border border-zinc-600 rounded-lg text-zinc-400 hover:bg-zinc-700/70 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 text-zinc-400 mx-auto mb-4 animate-spin" />
          <p className="text-zinc-400">Loading all locks...</p>
        </div>
      ) : tokenGroups.length === 0 ? (
        <div className="text-center py-8">
          <Lock className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
          <p className="text-zinc-400">No locks found</p>
          <p className="text-zinc-500 text-sm">No tokens have been locked yet</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {tokenGroups.map((group) => (
              <div key={group.tokenAddress} className="bg-zinc-700/30 rounded-lg border border-zinc-600">
                {/* Token Group Header */}
                <button
                  onClick={() => toggleGroup(group.tokenAddress)}
                  className="w-full p-4 flex items-center justify-between hover:bg-zinc-700/50 transition-colors rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-400/20 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-purple-400">
                        {group.symbol.charAt(0)}
                      </span>
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-mono text-white">{group.symbol}</h3>
                      <p className="text-zinc-400 text-sm font-mono">
                        {group.tokenAddress.slice(0, 6)}...{group.tokenAddress.slice(-4)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-white font-medium">
                        {formatTokenAmount(group.totalAmount)} {group.symbol}
                      </p>
                      <p className="text-zinc-400 text-sm">
                        {group.totalLocks} lock{group.totalLocks !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {group.isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-zinc-400" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-zinc-400" />
                    )}
                  </div>
                </button>

                {/* Collapsible Locks List */}
                {group.isExpanded && (
                  <div className="border-t border-zinc-600">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-700">
                            <th className="text-left py-3 px-4 text-zinc-300 font-medium">Owner</th>
                            <th className="text-left py-3 px-4 text-zinc-300 font-medium">Type</th>
                            <th className="text-left py-3 px-4 text-zinc-300 font-medium">Unlock Time</th>
                            <th className="text-left py-3 px-4 text-zinc-300 font-medium">Amount Locked</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.locks.map((lock) => (
                            <tr key={lock.id.toString()} className="border-b border-zinc-700/50 hover:bg-zinc-700/20 transition-colors">
                              <td className="py-3 px-4">
                                <span className="text-zinc-400 text-sm font-mono">
                                  {lock.owner.slice(0, 6)}...{lock.owner.slice(-4)}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  {getStatusIcon(lock.type)}
                                  <span className={getStatusColor(lock.type)}>{lock.type}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-zinc-400" />
                                  <span className="text-zinc-400 text-sm">
                                    {formatDate(BigInt(Math.floor(lock.unlockDate.getTime() / 1000)))}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-white font-medium">
                                  {formatTokenAmount(lock.amountLocked)} {lock.symbol}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-zinc-400">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalLocks)} of {totalLocks} locks
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 bg-zinc-700/50 border border-zinc-600 rounded-lg text-zinc-400 hover:bg-zinc-700/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-zinc-300 text-sm px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-zinc-700/50 border border-zinc-600 rounded-lg text-zinc-400 hover:bg-zinc-700/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
